import "./style.scss";

import React, { useEffect, useState } from "react";
import axios, { AxiosRequestConfig } from "axios";

import { Link } from "react-router-dom";
import Markdown from "react-markdown";
import { Spinner } from "../../components";
import remarkGfm from "remark-gfm";
import { timeAgo } from "../../utils/helpers";

export const NewsDisplay: React.FC = () => {
  const [newsData, setNewsData] = useState<any[]>();
  const [error, setError] = useState<string>();

  const fetchNews = async (abortController: AbortController) => {
    const notificationURL = `/api/news`;
    const opts: AxiosRequestConfig<any> = { signal: abortController?.signal };

    try {
      const { data } = await axios.get(notificationURL, opts);
      if (!data?.data) return;

      setNewsData(data.data);
    } catch (err) {
      setError("Failed to fetch the news");
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchNews(abortController);

    return () => {
      abortController.abort();
    };
  }, []);

  const maybeTransformWord = (val: string) => {
    // normal timestamp
    if (val.startsWith("<t:") && val.endsWith(":R>")) {
      // timestamp relative
      const formatter = new Intl.RelativeTimeFormat(`en`, { style: "long" });
      const now = +new Date() / 1000 / 60 / 60 / 24;
      const _timestamp = +("" + val).slice(3, -3);
      const then = _timestamp / 60 / 60 / 24;
      const days = then - now;
      const absDays = Math.abs(days);

      let output = "";

      if (absDays > 1) {
        output = formatter.format(+days.toFixed(0), "days");
      }

      if (absDays * 24 < 1) {
        output = formatter.format(+(days * 24 * 60).toFixed(0), "minutes");
      }

      if (absDays < 1) {
        output = formatter.format(+(days * 24).toFixed(0), "hours");
      }

      const staticTimestamp = new Intl.DateTimeFormat("en", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const staticTimestampDisplay = staticTimestamp.format(_timestamp * 1000);
      output += ` (${staticTimestampDisplay})`;

      return `\`${output}\``;
    }

    // handle emojis
    if (val.startsWith("<") && val.endsWith(">")) {
      const isAnimated = val.startsWith("<a:");
      const emojiName = val.split(":")?.[1];
      const emojiID = val.split(":")?.[2]?.slice(0, -1);
      const emojiSize = 20;
      const params = `?size=${emojiSize}${isAnimated ? "&animated=true" : ""}`;
      const baseURL = `https://cdn.discordapp.com/emojis/`;
      const emojiURL = `${baseURL}${emojiID}.webp${params}`;
      return `![${emojiName}](${emojiURL})`;
    }

    // normal text
    return val;
  };

  const maybeRelatifyTimestamps = (acc: string, val: string) => {
    val = maybeTransformWord(val);
    return acc + ` ${val}`;
  };

  const displayDiscordLikeMessage = (message: any) => {
    const clickableAuthor = false;

    let avatarBaseURL = "";
    let avatarHash = "";
    let profileLink = "";

    if (clickableAuthor) {
      avatarBaseURL = `https://cdn.discordapp.com/avatars/${message.author.id}/${message.author.avatar}`;
      avatarHash = message.author.avatar;
      profileLink = "/profile/701464050";
    } else {
      avatarHash = "b79e85432656b2e21f5d34b9d8a1c971";
      avatarBaseURL = `https://cdn.discordapp.com/avatars/806289907953434634/${avatarHash}`;
      profileLink = "#";
    }

    const isAnimated = avatarHash.startsWith("a_");
    const avatarURL = `${avatarBaseURL}.png`;
    const avatarGifURL = `${avatarBaseURL}.gif`;

    const createdAt = timeAgo(message.createdAt);
    const editedAt = timeAgo(message.editedAt);

    const content = message?.content
      ?.split(" ")
      .reduce(maybeRelatifyTimestamps, "");

    return (
      <div className="discord-like-msg" key={message.messageID}>
        <div className={`discord-like-left ${isAnimated ? "is-animated" : ""}`}>
          {clickableAuthor ? (
            <Link to={profileLink}>
              <img src={avatarURL} className="static-pfp" alt="pfp" />
              {isAnimated && (
                <img src={avatarGifURL} className="animated-pfp" alt="pfp" />
              )}
            </Link>
          ) : (
            <>
              <img src={avatarURL} className="static-pfp" alt="pfp" />
              {isAnimated && (
                <img src={avatarGifURL} className="animated-pfp" alt="pfp" />
              )}
            </>
          )}
        </div>
        <div className="discord-like-right">
          <div
            className={`discord-like-username ${clickableAuthor ? "pink" : ""}`}
          >
            {/* Mimee */}
            <div>
              {clickableAuthor ? (
                <Link to={profileLink}>{message.author.name}</Link>
              ) : (
                "Akasha"
              )}
            </div>
            {/* Today at 6:66 */}
            {/* 01/02/2024 16:20 */}
            <div title={message.editedAt ? `Edited - ${editedAt}` : createdAt}>
              {createdAt}
            </div>
          </div>
          <div className="discord-like-content">
            <Markdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>
              {content}
            </Markdown>
            {message.editedAt && (
              <span className="discord-like-edited-at" title={editedAt}>
                (edited){/* Monday, 5 January 2024 14:53 */}
                {/* {editedAt} */}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative news">
      <div className="discord-like-wrapper">
        {newsData ? (
          newsData?.map(displayDiscordLikeMessage)
        ) : (
          <div className="spinner-wrapper">{error || <Spinner />}</div>
        )}
      </div>
    </div>
  );
};
